import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

import type { SubsectionId } from "./types";

type OnboardingDraftContextValue = {
    setDraft: (id: SubsectionId, active: boolean) => void;
    draftIds: ReadonlySet<SubsectionId>;
};

const OnboardingDraftContext =
    createContext<OnboardingDraftContextValue | null>(null);

export function OnboardingDraftProvider({
    children
}: {
    children: ReactNode;
}): ReactNode {
    const [draftIds, setDraftIds] = useState<Set<SubsectionId>>(
        () => new Set()
    );

    const setDraft = useCallback((id: SubsectionId, active: boolean) => {
        setDraftIds((prev) => {
            const has = prev.has(id);
            if (active === has) return prev;
            const next = new Set(prev);
            if (active) next.add(id);
            else next.delete(id);
            return next;
        });
    }, []);

    const value = useMemo(() => ({ setDraft, draftIds }), [setDraft, draftIds]);

    return (
        <OnboardingDraftContext.Provider value={value}>
            {children}
        </OnboardingDraftContext.Provider>
    );
}

/** Forms call this so the left rail can show In progress while there are unsaved edits. */
export function useReportOnboardingDraft(
    subsectionId: SubsectionId,
    hasDraft: boolean
): void {
    const ctx = useContext(OnboardingDraftContext);
    useEffect(() => {
        if (!ctx) return;
        ctx.setDraft(subsectionId, hasDraft);
    }, [ctx, subsectionId, hasDraft]);
}

/** Hub-hoisted form state (e.g. use case) reports draft without mounting the form. */
export function useSetOnboardingDraft(
    subsectionId: SubsectionId,
    hasDraft: boolean
): void {
    const ctx = useContext(OnboardingDraftContext);
    useEffect(() => {
        if (!ctx) return;
        ctx.setDraft(subsectionId, hasDraft);
    }, [ctx, subsectionId, hasDraft]);
}

export function useOnboardingDraftIds(): ReadonlySet<SubsectionId> {
    return useContext(OnboardingDraftContext)?.draftIds ?? new Set();
}
